import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from 'dayjs';


import '../../assets/css/import.scss';
import '../../assets/css/locationModal.scss';
import { useTranslation } from "../../contexts/TranslationContext";
import api from '../../utils/api';
import FindLocation from '../UC/FindLocation';
import '../../config';

const ImportMeta = ({
  formInput,
  setFormInput,
  defineCategory,
  onImportClick,
  onResetClick,
  isImporting = false,
  isMainLoading = false,
  isEditMode = false
}) => {
  const { language } = useTranslation();

  // Helper function to sort arrays with "other" option last
  const sortWithOtherLast = (array) => {
    if (!array || !Array.isArray(array)) return [];

    return array.sort((a, b) => {
      const aIsOther = a.Name && a.Name.toLowerCase().includes('other');
      const bIsOther = b.Name && b.Name.toLowerCase().includes('other');

      if (aIsOther && !bIsOther) return 1;
      if (!aIsOther && bIsOther) return -1;
      return 0;
    });
  };

  const loginAccount = useMemo(() => {
    const stored = localStorage.getItem("FWUser");
    return stored ? JSON.parse(stored) : null;
  }, []);

  const defaultPosition = { lat: 39.8283, lng: -98.5795 }; // Center of United States

  // State for location modal
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [location, setLocation] = useState(''); // eslint-disable-line no-unused-vars
  const [locationData, setLocationData] = useState({ // eslint-disable-line no-unused-vars
    latitude: '',
    longitude: '',
    address: '',
    city: '',
    state: '',
    county: '',
    zipcode: '',
    country: ''
  });

  // Add state for API data
  const [formOptions, setFormOptions] = useState({
    countries: [],
    states: [],
    counties: [],
    recoveryEvents: [],
    eventTypes: [],
    categories: []
  });

  const [selectedCategory, setSelectedCategory] = useState(formInput?.Category || 0);

  const Category_RecoveredImpression = 2;
  // selectedCategory is now a constant since Category is hardcoded
  // const selectedCategory = Category_RecoveredImpression;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for unknown time checkbox
  const [isUnknownTime, setIsUnknownTime] = useState(false);

  // State for separate date and time
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // State for showing/hiding Scanner Information section
  const [showScannerInfo, setShowScannerInfo] = useState(false);

  // State for datetime picker modal
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);

  // State for time input values (separate from selectedTime for better input handling)
  const [hourInputValue, setHourInputValue] = useState('');
  const [minuteInputValue, setMinuteInputValue] = useState('');

  // Use a ref to track if we should run initialization
  const initializationRef = React.useRef(true);
  // Use a ref to track if we're in the middle of updating to prevent loops
  const updatingRef = React.useRef(false);
  // Create a ref for the modal container to manage the DatePicker's popper
  const dateTimeModalRef = React.useRef(null);

  // Set hardcoded Category on component mount
  useEffect(() => {
    // Only set default category if no category is set, don't force it
    if (!formInput.Category) {
      const defaultCategory = Category_RecoveredImpression; // Default to Recovered Impression
      setFormInput(prev => ({
        ...prev,
        Category: defaultCategory
      }));
      setSelectedCategory(defaultCategory);
    } else {
      // Ensure selectedCategory state matches formInput.Category
      setSelectedCategory(formInput.Category);
    }
  }, [formInput.Category, setFormInput]);

  // Fetch form values from API on component mount
  useEffect(() => {

    const fetchFormOptions = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const _tokenRequestAPI = localStorage.getItem("FWToken");

        const response = await api.post(
          `${global.config.api.domain}/fwiq-api/api/evidence/getpartialvalues`,
          {
            Token: _tokenRequestAPI,
            Fields: ["country", "state", "county", "recoveryevent", "eventtype", "category"]
          }
        );

        const data = response.data || {};



        // Format the data for the form with sorting
        setFormOptions({
          countries: data.Countries || [],
          states: data.States || [],
          counties: data.Counties || [],
          recoveryEvents: sortWithOtherLast(data.RecoveryEvents || []),
          eventTypes: sortWithOtherLast(data.EventTypes || []),
          categories: data.Categories || []
        });

        // Cache the data in localStorage for future use
        localStorage.setItem("form_options_cache", JSON.stringify({
          timestamp: Date.now(),
          data: {
            Countries: data.Countries || [],
            States: data.States || [],
            Counties: data.Counties || [],
            RecoveryEvents: data.RecoveryEvents || [],
            EventTypes: data.EventTypes || [],
            Categories: data.Categories || []
          }
        }));

      } catch (err) {
        console.error("Error fetching form options:", err);
        setError("Failed to load form options. Please refresh the page.");

        // Try to use cached data if available
        const cachedData = localStorage.getItem("form_options_cache");
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            setFormOptions({
              countries: parsed.data.Countries || [],
              states: parsed.data.States || [],
              counties: parsed.data.Counties || [],
              recoveryEvents: sortWithOtherLast(parsed.data.RecoveryEvents || []),
              eventTypes: sortWithOtherLast(parsed.data.EventTypes || []),
              categories: parsed.data.Categories || []
            });
          } catch (e) {
            console.error("Error parsing cached data:", e);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we have cached data that's less than 1 hour old
    const cachedData = localStorage.getItem("form_options_cache");

    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        const oneHour = 60 * 60 * 1000; // ms

        if (Date.now() - parsed.timestamp < oneHour) {
          // Use cached data if it's less than 1 hour old
          setFormOptions({
            countries: parsed.data.Countries || [],
            states: parsed.data.States || [],
            counties: parsed.data.Counties || [],
            recoveryEvents: sortWithOtherLast(parsed.data.RecoveryEvents || []),
            eventTypes: sortWithOtherLast(parsed.data.EventTypes || []),
            categories: parsed.data.Categories || []
          });
          return;
        }
      } catch (err) {
        console.error("Error parsing cached data:", err);
      }
    }

    // Fetch fresh data if no valid cache exists
    fetchFormOptions();
  }, []);

  // Set default country to United States when form options are loaded
  useEffect(() => {
    if (formOptions.countries.length > 0 && !formInput.Country) {
      // Find United States in the countries list
      const unitedStates = formOptions.countries.find(country =>
        country.Name === 'United States' ||
        country.Name === 'United States of America' ||
        country.Name === 'USA' ||
        country.Name === 'US'
      );

      if (unitedStates) {
        setFormInput(prev => ({
          ...prev,
          Country: unitedStates.ID
        }));
      }
    }
  }, [formOptions.countries, formInput.Country, setFormInput]);

  // Add state filtering based on selected country
  const [filteredStates, setFilteredStates] = useState([]);

  // Add county filtering based on selected state
  const [filteredCounties, setFilteredCounties] = useState([]);

  // Update filtered states when country changes
  useEffect(() => {
    if (formInput.Country && formOptions.states.length > 0) {

      const statesForCountry = formOptions.states.filter(
        state => parseInt(state.Country_ID) === parseInt(formInput.Country)
      );
      setFilteredStates(statesForCountry);

      // Clear state selection if current state doesn't belong to the selected country
      const stateExists = statesForCountry.some(state => parseInt(state.ID) === parseInt(formInput.State));
      if (!stateExists && formInput.State) {
        setFormInput(prev => ({
          ...prev,
          State: '',
          County: '' // Also clear county since state changed
        }));
      }
    } else {
      setFilteredStates([]);
    }
  }, [formInput.Country, formOptions.states, formInput.State, setFormInput]); // Only include specific fields needed

  // Update filtered counties when state changes
  useEffect(() => {
    if (formInput.State && formOptions.counties.length > 0) {


      const countiesForState = formOptions.counties.filter(
        county => parseInt(county.State_ID) === parseInt(formInput.State)
      );
      setFilteredCounties(countiesForState);

      // Clear county selection if current county doesn't belong to the selected state
      const countyExists = countiesForState.some(county => parseInt(county.ID) === parseInt(formInput.County));
      if (!countyExists && formInput.County) {
        setFormInput(prev => ({
          ...prev,
          County: ''
        }));
      }
    } else {
      setFilteredCounties([]);
    }
  }, [formInput.State, formOptions.counties, formInput.County, setFormInput]); // Only include specific fields needed

  // selectedCategory is now hardcoded to Category_RecoveredImpression
  // No need to update it based on formInput.Category since it's fixed

  // Initialize unknown time state and separate date/time based on formInput.DateOfIncident and TimeOfIncident
  // This effect handles initialization and external changes to DateOfIncident and TimeOfIncident
  useEffect(() => {
    // Only run initialization once
    if (initializationRef.current) {
      // Handle date initialization
      if (formInput.DateOfIncident) {
        // Check if it's already a dayjs object or needs conversion
        const dateValue = dayjs.isDayjs(formInput.DateOfIncident)
          ? formInput.DateOfIncident
          : dayjs(formInput.DateOfIncident);

        // Ensure the date is valid before setting
        if (dateValue.isValid()) {
          setSelectedDate(dateValue);
        } else {
          console.warn('Invalid date value, setting to null');
          setSelectedDate(null);
        }
      } else {
        setSelectedDate(null);
      }

      // Handle time initialization - check TimeOfIncident field
      if (formInput.TimeOfIncident === -1) {
        setIsUnknownTime(true);
        setSelectedTime(null);
        setHourInputValue('');
        setMinuteInputValue('');
      } else if (formInput.TimeOfIncident && typeof formInput.TimeOfIncident === 'number') {
        // TimeOfIncident is in seconds since midnight
        setIsUnknownTime(false);
        const hours = Math.floor(formInput.TimeOfIncident / 3600);
        const minutes = Math.floor((formInput.TimeOfIncident % 3600) / 60);
        const timeValue = dayjs().hour(hours).minute(minutes).second(0);
        setSelectedTime(timeValue);
        setHourInputValue(timeValue.format('h'));
        setMinuteInputValue(timeValue.format('mm'));
      } else if (formInput.DateOfIncident && !formInput.TimeOfIncident) {
        // Legacy support: if DateOfIncident exists but no TimeOfIncident, extract time from DateOfIncident
        setIsUnknownTime(false);
        const dateValue = dayjs.isDayjs(formInput.DateOfIncident)
          ? formInput.DateOfIncident
          : dayjs(formInput.DateOfIncident);

        // Ensure the date is valid before using
        if (dateValue.isValid()) {
          setSelectedTime(dateValue);
          setHourInputValue(dateValue.format('h'));
          setMinuteInputValue(dateValue.format('mm'));
        } else {
          setSelectedTime(null);
          setHourInputValue('');
          setMinuteInputValue('');
        }
      } else {
        setIsUnknownTime(false);
        setSelectedTime(null);
        setHourInputValue('');
        setMinuteInputValue('');
      }

      // After initialization, mark as initialized
      initializationRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once on mount

  // Separate effect to handle external updates to formInput.DateOfIncident in edit mode
  useEffect(() => {
    // Skip during initialization phase
    if (initializationRef.current) {
      return;
    }

    // Process date change if there's a new DateOfIncident value different from current selectedDate
    if (formInput.DateOfIncident) {
      const dateValue = dayjs.isDayjs(formInput.DateOfIncident)
        ? formInput.DateOfIncident
        : dayjs(formInput.DateOfIncident);

      // Only update if the new value is valid and different from current value
      if (dateValue.isValid()) {
        const currentDateStr = selectedDate ? selectedDate.format('YYYY-MM-DD') : '';
        const newDateStr = dateValue.format('YYYY-MM-DD');

        if (currentDateStr !== newDateStr) {
          setSelectedDate(dateValue);
        }
      } else {
        console.warn('Invalid date value in formInput, not updating selectedDate');
      }
    } else if (selectedDate !== null) {
      // Clear selectedDate if formInput.DateOfIncident is nullified
      setSelectedDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formInput.DateOfIncident, selectedDate]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    setFormInput(prev => {
      const newFormInput = { ...prev };

      // Handle form input value updates
      if (name === 'State') {
        newFormInput[name] = parseInt(value);
        newFormInput.County = 0; // Reset county when state changes
      } else if (name === 'Category') {
        // Convert category to integer and update both form and selected category
        const categoryValue = parseInt(value);
        newFormInput[name] = categoryValue;
        setSelectedCategory(categoryValue); // Update selectedCategory state immediately
      } else {
        newFormInput[name] = value;
      }

      // Handle special cases that need additional state updates
      if (name === 'Country') {
        newFormInput.State = 0; // Reset state when country changes
        newFormInput.County = 0; // Reset county when country changes
      }

      return newFormInput;
    });

    // Handle additional state updates that don't require form input changes
    if (name === 'Country') {
      setFilteredStates(formOptions.states.filter(state => state.Country_ID === parseInt(value)));
      setFilteredCounties([]); // Reset counties since state will change
    }

    if (name === 'State') {
      setFilteredCounties(formOptions.counties.filter(county => county.State_ID === parseInt(value)));
    }
  }, [formOptions.states, formOptions.counties, setFormInput]);

  // Handle date change from DatePicker component
  const handleDateChange = useCallback((date) => {
    // Skip update if the date hasn't actually changed
    if (date && selectedDate && dayjs.isDayjs(date) && dayjs.isDayjs(selectedDate)) {
      if (date.format('YYYY-MM-DD') === selectedDate.format('YYYY-MM-DD')) {
        return;
      }
    }

    // Directly update the selected date state
    setSelectedDate(date);

    // Update the formInput with the new date
    if (date) {
      setFormInput(prev => {
        // Skip update if the date is the same to prevent loops
        if (prev.DateOfIncident && dayjs.isDayjs(prev.DateOfIncident) &&
          date.format('YYYY-MM-DD') === prev.DateOfIncident.format('YYYY-MM-DD')) {
          return prev;
        }
        return { ...prev, DateOfIncident: date };
      });
    } else {
      setFormInput(prev => ({ ...prev, DateOfIncident: null }));
    }
  }, [selectedDate, setFormInput]);

  // Handle time change
  const handleTimeChange = useCallback((time) => {
    // Prevent loops by marking that we're updating
    updatingRef.current = true;

    setSelectedTime(time);

    // Update input values immediately for better UX
    if (time) {
      setHourInputValue(time.format('h'));
      setMinuteInputValue(time.format('mm'));
    }

    // Convert time to seconds since midnight for TimeOfIncident
    if (time) {
      const timeInSeconds = time.hour() * 3600 + time.minute() * 60;
      setFormInput(prev => ({ ...prev, TimeOfIncident: timeInSeconds }));
    } else {
      setFormInput(prev => ({ ...prev, TimeOfIncident: null }));
    }

    // Allow updates again after a brief delay
    setTimeout(() => {
      updatingRef.current = false;
    }, 0);
  }, [setFormInput]);

  // Handle unknown time checkbox change
  const handleUnknownTimeChange = useCallback((e) => {
    const checked = e.target.checked;

    // Prevent loops by marking that we're updating
    updatingRef.current = true;

    // Disable initialization effect since user is now interacting
    initializationRef.current = false;

    setIsUnknownTime(checked);

    if (checked) {
      // Set TimeOfIncident to -1 for unknown time, keep DateOfIncident with date only
      setSelectedTime(null);
      setHourInputValue('');
      setMinuteInputValue('');
      setFormInput(prev => ({ ...prev, TimeOfIncident: -1 }));

      // Ensure DateOfIncident has a valid date (if not already set)
      if (!selectedDate) {
        // If no date selected, prompt user or set to current date
        const today = dayjs().startOf('day');
        setSelectedDate(today);
        setFormInput(prev => ({ ...prev, DateOfIncident: today }));
      }
    } else {
      // use formInput.TimeOfIncident to set selectedTime if available
      if (formInput.TimeOfIncident && typeof formInput.TimeOfIncident === 'number' && formInput.TimeOfIncident >= 0) {
        const hours = Math.floor(formInput.TimeOfIncident / 3600);
        const minutes = Math.floor((formInput.TimeOfIncident % 3600) / 60);
        const timeValue = dayjs().hour(hours).minute(minutes).second(0);
        setSelectedTime(timeValue);
        setHourInputValue(timeValue.format('h'));
        setMinuteInputValue(timeValue.format('mm'));

        const timeInSeconds = timeValue.hour() * 3600 + timeValue.minute() * 60;
        setFormInput(prev => ({ ...prev, TimeOfIncident: timeInSeconds }));
      }

    }

    // Allow updates again after a brief delay
    setTimeout(() => {
      updatingRef.current = false;
    }, 0);
  }, [selectedDate, setFormInput, formInput.TimeOfIncident]);

  // Open the location modal
  const handleOpenLocationModal = useCallback(() => {
    setShowLocationModal(true);
  }, []);

  // Close the location modal
  const handleCloseLocationModal = useCallback(() => {
    setShowLocationModal(false);
  }, []);

  // Toggle Scanner Information section visibility
  const toggleScannerInfo = useCallback(() => {
    setShowScannerInfo(prev => !prev);
  }, []);

  // Open the datetime picker modal
  const handleOpenDateTimeModal = useCallback(() => {
    // Check if TimeOfIncident is -1 (unknown time) and set the checkbox state accordingly
    if (formInput.TimeOfIncident === -1) {
      setIsUnknownTime(true);
      setSelectedTime(null);
      setHourInputValue('');
      setMinuteInputValue('');
    } else {
      // Initialize time inputs when opening modal
      if (selectedTime) {
        setHourInputValue(selectedTime.format('h'));
        setMinuteInputValue(selectedTime.format('mm'));
      } else if (!isUnknownTime) {
        // Check if there's a valid time in formInput.TimeOfIncident before defaulting
        if (formInput.TimeOfIncident && typeof formInput.TimeOfIncident === 'number' && formInput.TimeOfIncident !== -1) {
          // Convert TimeOfIncident (seconds since midnight) to time
          const hours = Math.floor(formInput.TimeOfIncident / 3600);
          const minutes = Math.floor((formInput.TimeOfIncident % 3600) / 60);
          const timeFromForm = dayjs().hour(hours).minute(minutes).second(0);
          setSelectedTime(timeFromForm);
          setHourInputValue(timeFromForm.format('h'));
          setMinuteInputValue(timeFromForm.format('mm'));
        } else {
          // Set default time if no time is selected and time is not unknown
          const defaultTime = dayjs().hour(9).minute(0).second(0);
          setSelectedTime(defaultTime);
          setHourInputValue('9');
          setMinuteInputValue('00');
        }
      }
    }
    setShowDateTimeModal(true);
  }, [selectedTime, isUnknownTime, formInput.TimeOfIncident]);

  // Close the datetime picker modal
  const handleCloseDateTimeModal = useCallback(() => {
    setShowDateTimeModal(false);
  }, []);

  // Handle datetime confirmation from modal
  const handleDateTimeConfirm = useCallback(() => {
    // Prevent loops by marking that we're updating
    updatingRef.current = true;

    // Ensure the form input is properly updated with separate date and time
    if (selectedDate) {
      setFormInput(prev => ({ ...prev, DateOfIncident: selectedDate }));
    }

    if (isUnknownTime) {
      setFormInput(prev => ({ ...prev, TimeOfIncident: -1 }));
    } else if (selectedTime) {
      const timeInSeconds = selectedTime.hour() * 3600 + selectedTime.minute() * 60;
      setFormInput(prev => ({ ...prev, TimeOfIncident: timeInSeconds }));
    } else {
      // Default to start of day if no time selected
      setFormInput(prev => ({ ...prev, TimeOfIncident: 0 }));
    }

    setShowDateTimeModal(false);

    // Allow updates again after a brief delay
    setTimeout(() => {
      updatingRef.current = false;
    }, 0);
  }, [selectedDate, selectedTime, isUnknownTime, setFormInput]);

  // Format datetime for display in input field
  const formatDateTimeDisplay = useCallback(() => {
    if (!selectedDate && !formInput.DateOfIncident) {
      return '';
    }

    const dateToDisplay = selectedDate || (formInput.DateOfIncident ? dayjs(formInput.DateOfIncident) : null);

    if (isUnknownTime || formInput.TimeOfIncident === -1) {
      return dateToDisplay ? dateToDisplay.format('MM/DD/YYYY') : '';
    }

    if (dateToDisplay && selectedTime) {
      return dateToDisplay.format('MM/DD/YYYY') + ' ' + selectedTime.format('h:mm A');
    }

    if (dateToDisplay && formInput.TimeOfIncident && typeof formInput.TimeOfIncident === 'number') {
      const hours = Math.floor(formInput.TimeOfIncident / 3600);
      const minutes = Math.floor((formInput.TimeOfIncident % 3600) / 60);
      const timeValue = dayjs().hour(hours).minute(minutes);
      return dateToDisplay.format('MM/DD/YYYY') + ' ' + timeValue.format('h:mm A');
    }

    if (dateToDisplay) {
      return dateToDisplay.format('MM/DD/YYYY');
    }

    return '';
  }, [selectedDate, selectedTime, isUnknownTime, formInput.DateOfIncident, formInput.TimeOfIncident]);

  // Handle location selection from FindLocation component
  const handleLocationSelect = useCallback((location, addressData) => {
    // Extract location data from geocoding results
    const { lat, lng, formattedAddress, country, state, county } = addressData;

    // Update location data state
    setLocationData({
      address: formattedAddress,
      country,
      state,
      county,
      lat,
      lng
    });

    // Close the modal
    setShowLocationModal(false);

    // match country, state, and county with form input to add IDs (case insensitive)
    // let countryMatch = formOptions.countries.find(c => c.Name.toLowerCase() === country.toLowerCase());

    // if(!countryMatch) {
    //   countryMatch = 1; // Default to United States if no match found
    // }

    const countryID = 1; // Default to United States

    const stateMatch = formOptions.states.find(s => s.Name.toLowerCase() === state.toLowerCase());
    const countyMatch = formOptions.counties.find(c => c.Name.toLowerCase() === county.toLowerCase() && c.State_ID === stateMatch?.ID);
    const stateID = stateMatch ? stateMatch.ID : 0;
    const countyID = countyMatch ? countyMatch.ID : 0;

    // Update filtered states and counties to reflect the new selections
    const statesForCountry = formOptions.states.filter(
      state => parseInt(state.Country_ID) === parseInt(countryID)
    );
    setFilteredStates(statesForCountry);

    if (stateID > 0) {
      const countiesForState = formOptions.counties.filter(
        county => parseInt(county.State_ID) === parseInt(stateID)
      );
      setFilteredCounties(countiesForState);
    } else {
      setFilteredCounties([]);
    }

    // Update form input with location details
    setFormInput(prev => ({
      ...prev,
      LocationIncident: formattedAddress,
      Country: countryID,
      State: stateID,
      County: countyID,
      Latitude: lat.toString(),
      Longitude: lng.toString()
    }));
  }, [formOptions.states, formOptions.counties, setFormInput, setFilteredStates, setFilteredCounties]);

  return (
    <div className="import-meta">
      <div className="group-title">{language?.import?.impressionInformation || 'Impression Evidence Information'}</div>

      {/* Category is now hardcoded to Category_RecoveredImpression = 2 */}
      <div className="a-meta">
        <div className="meta-title">{language?.import?.category || 'Category'}</div>
        <div className="meta-value">
          <select 
            name="Category" 
            onChange={handleInputChange} 
            value={formInput.Category?.toString() || ''}
          >
            <option value="">{language?.import?.selectCategory || 'Select category'}</option>
            <option value="2">{language?.import?.recoveredImpression || 'Recovered Impression'}</option>
            <option value="3">{language?.import?.testShoe || 'Test Shoe (TS)'}</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fa fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {/* Case information */}
      <div className="a-meta">
        <div className="meta-title required">{language?.import?.caseIncident || 'Case Incident#'}</div>
        <div className="meta-value">
          <input
            type="text"
            className="style-textbox-ol"
            name="CaseIncident"
            onChange={handleInputChange}
            value={formInput.CaseIncident || ''}
            placeholder={language?.import?.enterCaseIncident || 'Enter case incident number'}
          />
        </div>
      </div>
      <div className="a-meta">
        <div className="meta-title required">{language?.import?.impressionEvidence || 'Impression Evidence #'}</div>
        <div className="meta-value">
          <input
            type="text"
            className="style-textbox-ol"
            name="CartridgeCaseNumber"
            onChange={handleInputChange}
            value={formInput.CartridgeCaseNumber || ''}
            placeholder={language?.import?.enterCartridgeNumber || 'Enter cartridge case number'}
          />
        </div>
      </div>

      {/* Event type - NEW */}
      {/* <div className="a-meta">
        <div className="meta-title">{language?.import?.eventType || 'Event Type'}</div>
        <div className="meta-value">
          <select
            name="EventType"
            onChange={handleInputChange}
            value={formInput.EventType || ''}
          >
            <option value="">{language?.import?.selectEventType || 'Select event type'}</option>
            {formOptions.eventTypes.map(eventType => (
              <option key={eventType.ID} value={eventType.Name}>
                {eventType.Name}
              </option>
            ))}
          </select>
        </div>
      </div> */}

      {/* Recovery Event - NEW */}
      <div className="a-meta">
        <div className="meta-title">{language?.import?.recoveryEvent || 'Recovery Event'}</div>
        <div className="meta-value">
          <select
            name="RecoveryEvent"
            onChange={handleInputChange}
            value={formInput.RecoveryEvent || ''}
          >
            <option value="">{language?.import?.selectRecoveryEvent || 'Select recovery event'}</option>
            {formOptions.recoveryEvents.map(event => (
              <option key={event.ID} value={event.ID}>
                {event.Name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Date of incident */}
      <div className="a-meta">
        <div className="meta-title required">{language?.import?.datetimeIncident || 'Date/Time of Incident'}</div>
        <div className="meta-value">
          <div className="location-input-wrapper">
            <input
              type="text"
              className="style-textbox-ol datetime-input"
              readOnly={true}
              onFocus={handleOpenDateTimeModal}
              value={formatDateTimeDisplay()}
              placeholder={language?.import?.selectDateTime || 'Select date and time'}
            />
            <button
              type="button"
              className="map-button"
              onClick={handleOpenDateTimeModal}
              title={language?.import?.selectDateTime || "Select date and time"}
            >
              <i className="fa fa-calendar"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Location of incident */}
      <div className="a-meta">
        <div className="meta-title required">{language?.import?.locationIncident || 'Location of Incident'}</div>
        <div className="meta-value">
          <div className="location-input-wrapper">
            <input
              type="text"
              className="style-textbox-ol"
              name="LocationIncident"
              readOnly={true}
              onFocus={handleOpenLocationModal}
              value={formInput.LocationIncident || ''}
              placeholder={language?.import?.enterLocationIncident || 'Enter location of incident'}
            />
            <button
              type="button"
              className="map-button"
              onClick={handleOpenLocationModal}
              title={language?.import?.findOnMap || "Find on map"}
            >
              <i className="fa fa-map"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Country */}
      <div className="a-meta">
        <div className="meta-title">{language?.import?.country || 'Country'}</div>
        <div className="meta-value">
          <select
            name="Country"
            onChange={handleInputChange}
            value={formInput.Country || 0}
            disabled={true}
            readOnly={true}
          >
            <option value="">{language?.import?.selectCountry || 'Select country'}</option>
            {isLoading ? (
              <option disabled>Loading countries...</option>
            ) : (
              formOptions.countries.map(country => (
                <option key={country.ID} value={country.ID}>
                  {country.Name}
                </option>
              )))
            }
          </select>
        </div>
      </div>

      {/* State */}
      <div className="a-meta">
        <div className="meta-title">{language?.import?.state || 'State'}</div>
        <div className="meta-value">
          <select
            name="State"
            onChange={handleInputChange}
            value={formInput.State || 0}
            disabled={true}
            readOnly={true}
          >
            <option value="">{language?.import?.selectState || 'Select state'}</option>
            {isLoading && filteredStates && formInput.State ? (
              <option disabled>Loading states...</option>
            ) : (
              filteredStates.map(state => (
                <option key={state.ID} value={state.ID}>
                  {state.Name}
                </option>
              )))
            }
          </select>
        </div>
      </div>

      {/* County */}
      <div className="a-meta">
        <div className="meta-title">{language?.import?.county || 'County'}</div>
        <div className="meta-value">
          <select
            name="County"
            onChange={handleInputChange}
            value={formInput.County || 0}
            disabled={true}
            readOnly={true}
          >
            <option value="">{language?.import?.selectCounty || 'Select county'}</option>
            {isLoading ? (
              <option disabled>Loading counties...</option>
            ) : (
              filteredCounties.map(county => (
                <option key={county.ID} value={county.ID}>
                  {county.Name}
                </option>
              )))
            }
          </select>
        </div>
      </div>
      <div className="a-meta">
        <div className="meta-title">{language?.import?.caseNotes || 'Case Notes'}</div>
        <div className="meta-value">
          <textarea
            className="style-textbox-ol"
            rows="4"
            name="CaseNotes"
            onChange={handleInputChange}
            value={formInput.CaseNotes || ''}
            placeholder={language?.import?.enterCaseNotes || 'Enter case notes'}
          ></textarea>
        </div>
      </div>

      {/* Hidden fields for coordinates */}
      <input type="hidden" name="Latitude" value={formInput.Latitude || ''} />
      <input type="hidden" name="Longitude" value={formInput.Longitude || ''} />

      {/* Rest of the form remains the same */}

      <div className="product-information-section" style={{ display: parseInt(selectedCategory) === defineCategory.TestShoe ? 'block' : 'none' }}>
        <div className="group-title">{language?.import?.productInformation || 'Footwear Product Information'}</div>

        <div className="a-meta">
          <div id="lblMake" className="meta-title required">{language?.import?.make || 'Make'}</div>
          <div className="meta-value">
            <input
              type="text"
              className="style-textbox-ol"
              name="Make"
              onChange={handleInputChange}
              value={formInput.Make || ''}
              placeholder={language?.import?.enterMake || 'Enter make'}
            />
          </div>

        </div>
        <div className="a-meta">
          <div id="lblShoeType" className="meta-title required">{language?.import?.shoeType || 'Shoe Type'}</div>
          <div className="meta-value">
            <input
              type="text"
              className="style-textbox-ol"
              name="ShoeType"
              onChange={handleInputChange}
              value={formInput.ShoeType || ''}
              placeholder={language?.import?.enterShoeType || 'Enter shoe type'}
            />
          </div>
        </div>
        <div className="a-meta">
          <div id="lblModel" className="meta-title required">{language?.import?.model || 'Model'}</div>
          <div className="meta-value">
            <input
              type="text"
              className="style-textbox-ol"
              name="Model"
              onChange={handleInputChange}
              value={formInput.Model || ''}
              placeholder={language?.import?.enterModel || 'Enter model'}
            />
          </div>
        </div>
        <div className="a-meta">
          <div className="meta-title">{language?.import?.productID || 'Product ID'}</div>
          <div className="meta-value">
            <input
              type="text"
              className="style-textbox-ol"
              name="ProductCode"
              onChange={handleInputChange}
              value={formInput.ProductCode || ''}
              placeholder={language?.import?.enterProductCode || 'Enter product id'}
            />
          </div>
        </div>
        <div className="a-meta">
          <div className="meta-title">{language?.import?.color || 'Color'}</div>
          <div className="meta-value">
            <input
              type="text"
              className="style-textbox-ol"
              name="Color"
              onChange={handleInputChange}
              value={formInput.Color || ''}
              placeholder={language?.import?.enterColor || 'Enter color'}
            />
          </div>
        </div>

        <div className="a-meta">
          <div className="meta-title">{language?.import?.EventType || 'Event Type'}</div>
          <div className="meta-value">
            <select
              name="EventType"
              onChange={handleInputChange}
              value={formInput.EventType || ''}
            >
              <option value="">{language?.import?.selectEventType || 'Select event type'}</option>
              {formOptions.eventTypes.map(eventType => (
                <option key={eventType.ID} value={eventType.ID}>
                  {eventType.Name}
                </option>
              ))
              }
            </select>
          </div>
        </div>
      </div>

      {/* Scanner Information Section */}
      <div className="product-information-section">
        <div className="group-title scanner-info-header" onClick={toggleScannerInfo}>
          <span>{language?.import?.scannerInfo || 'Scanner Information'}</span>
          <i className={`fa ${showScannerInfo ? 'fa-chevron-up' : 'fa-chevron-down'} toggle-icon`}></i>
        </div>

        {showScannerInfo && (
          <div className="scanner-info-content">



            {/* Entered By */}
            <div className="a-meta">
              <div className="meta-title">{language?.import?.importedBy || 'Imported By'}</div>
              <div className="meta-value">
                <input
                  type="text"
                  className="style-textbox-ol"
                  name="EnteredBy"
                  onChange={handleInputChange}
                  value={loginAccount?.Information.Name || ''}
                  readOnly={true}
                />
              </div>
            </div>

            {/* Phone # */}
            <div className="a-meta">
              <div className="meta-title">{language?.import?.phone || 'Phone #'}</div>
              <div className="meta-value">
                <input
                  type="tel"
                  className="style-textbox-ol"
                  name="Phone"
                  onChange={handleInputChange}
                  value={loginAccount?.Information.Phone || ''}
                  readOnly={true}
                />
              </div>
            </div>

            {/* Email(s) */}
            <div className="a-meta">
              <div className="meta-title">{language?.import?.email || 'Email(s)'}</div>
              <div className="meta-value">
                <input
                  type="email"
                  className="style-textbox-ol"
                  name="Email"
                  onChange={handleInputChange}
                  value={loginAccount?.Information.Email || ''}
                  readOnly={true}
                />
              </div>
            </div>

            {/* Agency */}
            <div className="a-meta">
              <div className="meta-title">{language?.import?.agency || 'Agency'}</div>
              <div className="meta-value">
                <input
                  type="text"
                  className="style-textbox-ol"
                  name="Agency"
                  onChange={handleInputChange}
                  value={loginAccount?.Information.AgencyName || ''}
                  readOnly={true}
                />
              </div>
            </div>
            {/* Investigator Assigned */}
            {/* <div className="a-meta">
          <div className="meta-title">{language?.import?.investorAssign || 'Detective/Investigator Assigned?'}</div>
          <div className="meta-value">
            <div className="radio-group">
              <div className="radio-option">
                <input
                  type="radio"
                  id="InvestigatorAssignedYes"
                  name="InvestigatorAssigned"
                  value="yes"
                  checked={formInput.InvestigatorAssigned === true}
                  onChange={() => setFormInput({ ...formInput, InvestigatorAssigned: true })}
                />
                <label htmlFor="InvestigatorAssignedYes">{language?.import?.yes || 'Yes'}</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="InvestigatorAssignedNo"
                  name="InvestigatorAssigned"
                  value="no"
                  checked={formInput.InvestigatorAssigned === false || formInput.InvestigatorAssigned === undefined}
                  onChange={() => setFormInput({ ...formInput, InvestigatorAssigned: false })}
                />
                <label htmlFor="InvestigatorAssignedNo">{language?.import?.no || 'No'}</label>
              </div>
            </div>
          </div>
        </div> */}

            {/* Conditional Investigator Information */}
            {/* {formInput.InvestigatorAssigned && (
          <div className="investigator-section">
            <div className="a-meta">
              <div className="meta-title required">{language?.import?.name || 'Name'}</div>
              <div className="meta-value">
                <input
                  type="text"
                  className="style-textbox-ol"
                  name="InvestigatorName"
                  onChange={handleInputChange}
                  value={formInput.InvestigatorName || ''}
                />
              </div>
            </div>

            <div className="a-meta">
              <div className="meta-title required">{language?.import?.phone || 'Phone #'}</div>
              <div className="meta-value">
                <input
                  type="tel"
                  className="style-textbox-ol"
                  name="InvestigatorPhone"
                  onChange={handleInputChange}
                  value={formInput.InvestigatorPhone || ''}
                />
              </div>
            </div>

            <div className="a-meta">
              <div className="meta-title required">{language?.import?.emails || 'Email(s)'}</div>
              <div className="meta-value">
                <input
                  type="email"
                  className="style-textbox-ol"
                  name="InvestigatorEmail"
                  onChange={handleInputChange}
                  value={formInput.InvestigatorEmail || ''}
                />
              </div>
            </div>

            <div className="a-meta">
              <div className="meta-title required">{language?.import?.agency || 'Agency'}</div>
              <div className="meta-value">
                <input
                  type="text"
                  className="style-textbox-ol"
                  name="InvestigatorAgency"
                  onChange={handleInputChange}
                  value={formInput.InvestigatorAgency || ''}
                />
              </div>
            </div>
          </div>
        )} */}

            {/* <div className="a-meta">
          <div className="meta-title required">{language?.import?.EmailsForNotifications || 'Email Notification'}</div>
          <div className="meta-value">
            <textarea
              className="style-textbox-ol"
              rows="4"
              name="EmailsForNotifications"
              onChange={handleInputChange}
              value={formInput.EmailsForNotifications || ''}
            ></textarea>
          </div>
        </div> */}

          </div>
        )}
      </div>

      {/* Import/Update and Reset Buttons */}
      <div className="form-import-buttons">
        <button
          className={`btn btn-success ${isImporting ? 'importing' : ''}`}
          id="btnImport"
          type="button"
          onClick={onImportClick}
          disabled={isImporting || isMainLoading}
        >
          {isImporting ? (
            <>
              <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
              {isEditMode ? (language.import?.updating || 'Updating...') : (language.import?.importing || 'Importing...')}
            </>
          ) : (
            <>
              <i className={`fa ${isEditMode ? 'fa-save' : 'fa-plus'}`} aria-hidden="true"></i>
              {isEditMode ? (language.import?.update || 'Update') : (language.import?.import || 'Import')}
            </>
          )}
        </button>

        {onResetClick && (
          <button
            className="btn btn-secondary"
            id="btnReset"
            type="button"
            onClick={onResetClick}
            disabled={isImporting || isMainLoading}
          >
            <i className="fa fa-refresh" aria-hidden="true"></i>
            {language.import?.reset || 'Reset'}
          </button>
        )}
      </div>

      {/* DateTime Picker Modal */}
      {showDateTimeModal && (
        <div className="datetime-modal-backdrop">
          <div className="datetime-modal" ref={dateTimeModalRef}>
            <div className="datetime-modal-header">
              <h3>{language?.import?.selectDateTime || 'Select Date and Time'}</h3>
              <button
                className="close-button"
                onClick={handleCloseDateTimeModal}
              >
                ×
              </button>
            </div>
            <div className="datetime-modal-body">
              <div className="datetime-picker-container">
                {/* Date Picker */}
                <div className="date-picker-wrapper">
                  <label className="date-picker-label">{language?.import?.selectDate || 'Select Date'}</label>
                  <DatePicker
                    selected={selectedDate?.toDate() || null}
                    onChange={(date) => handleDateChange(date ? dayjs(date) : null)}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select date"

                    showPopperArrow={false}
                    popperPlacement="bottom-start"
                    popperContainer={({ children }) => (
                      <div>{children}</div>
                    )}
                  />
                </div>

                {/* Time Input - only show if unknown time is not checked */}
                {!isUnknownTime && (
                  <div className="time-input-wrapper">
                    <label className="time-input-label">{language?.import?.selectTime || 'Select Time'}</label>
                    <div className="time-input-container">
                      <div className="time-input-group">
                        <input
                          type="number"
                          className="time-input hour-input"
                          min="1"
                          max="12"
                          value={hourInputValue}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setHourInputValue(inputValue);
                          }}
                          onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue && !isNaN(parseInt(inputValue))) {
                              const hour = parseInt(inputValue);
                              if (hour >= 1 && hour <= 12) {
                                const currentTime = selectedTime || dayjs().hour(9).minute(0).second(0);
                                const isPM = currentTime.hour() >= 12;
                                let hour24 = hour;
                                if (isPM && hour !== 12) hour24 = hour + 12;
                                if (!isPM && hour === 12) hour24 = 0;
                                const newTime = currentTime.hour(hour24);
                                handleTimeChange(newTime);
                              } else {
                                // Reset to current time or default
                                const defaultHour = selectedTime ? selectedTime.format('h') : '9';
                                setHourInputValue(defaultHour);
                              }
                            } else {
                              // Reset to current time or default
                              const defaultHour = selectedTime ? selectedTime.format('h') : '9';
                              setHourInputValue(defaultHour);
                              if (!selectedTime) {
                                const defaultTime = dayjs().hour(9).minute(0).second(0);
                                handleTimeChange(defaultTime);
                              }
                            }
                          }}
                          placeholder="12"
                          autoComplete="off"
                        />
                        <span className="time-separator">:</span>
                        <input
                          type="number"
                          className="time-input minute-input"
                          min="0"
                          max="59"
                          value={minuteInputValue}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            setMinuteInputValue(inputValue);
                          }}
                          onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue !== '' && !isNaN(parseInt(inputValue))) {
                              const minute = parseInt(inputValue);
                              if (minute >= 0 && minute <= 59) {
                                const currentTime = selectedTime || dayjs().hour(9).minute(0).second(0);
                                const newTime = currentTime.minute(minute);
                                handleTimeChange(newTime);
                              } else {
                                // Reset to current time or default
                                const defaultMinute = selectedTime ? selectedTime.format('mm') : '00';
                                setMinuteInputValue(defaultMinute);
                              }
                            } else {
                              // Reset to current time or default
                              const defaultMinute = selectedTime ? selectedTime.format('mm') : '00';
                              setMinuteInputValue(defaultMinute);
                              if (!selectedTime) {
                                const defaultTime = dayjs().hour(9).minute(0).second(0);
                                handleTimeChange(defaultTime);
                              }
                            }
                          }}
                          placeholder="00"
                          autoComplete="off"
                        />
                        <select
                          className="time-input ampm-select"
                          value={selectedTime ? (selectedTime.hour() >= 12 ? 'PM' : 'AM') : 'AM'}
                          onChange={(e) => {
                            const currentTime = selectedTime || dayjs().hour(9).minute(0).second(0);
                            const isPM = e.target.value === 'PM';
                            let hour = currentTime.hour();

                            // Convert to 12-hour format first
                            const hour12 = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);

                            // Then convert back to 24-hour based on AM/PM selection
                            if (isPM) {
                              hour = hour12 === 12 ? 12 : hour12 + 12;
                            } else {
                              hour = hour12 === 12 ? 0 : hour12;
                            }

                            const newTime = currentTime.hour(hour);
                            handleTimeChange(newTime);
                          }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      <div className="time-presets">
                        <button
                          type="button"
                          className="preset-btn"
                          onClick={() => {
                            const now = dayjs();
                            handleTimeChange(now);
                          }}
                        >
                          Now
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Unknown time checkbox */}
                <div className="unknown-time-checkbox">
                  <input
                    type="checkbox"
                    id="unknownTimeModal"
                    checked={isUnknownTime}
                    onChange={handleUnknownTimeChange}
                  />
                  <label htmlFor="unknownTimeModal">{language?.import?.unknownTime || 'Unknown Time'}</label>
                </div>
              </div>
            </div>
            <div className="datetime-modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseDateTimeModal}
              >
                {language?.import?.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDateTimeConfirm}
              >
                {language?.import?.confirm || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="location-modal-backdrop">
          <div className="location-modal">
            <div className="location-modal-header">
              <h3>{language?.import?.findLocation || 'Find Location'}</h3>
              <button
                className="close-button"
                onClick={handleCloseLocationModal}
              >
                ×
              </button>
            </div>
            <div className="location-modal-body">
              <FindLocation
                apiKey={global.config.api.googleMapsApiKey || ""}
                addressInput={formInput.LocationIncident || ""}
                defaultLocation={{
                  lat: parseFloat(formInput?.Latitude) || defaultPosition.lat,
                  lng: parseFloat(formInput?.Longitude) || defaultPosition.lng
                }}
                onLocationSelect={(location, addressComponents) => {
                  handleLocationSelect(location, extractAddressData(addressComponents));
                }}
                placeholder={language?.import?.searchLocation || "Search for a location..."}
                height="400px"
                skipInitialMap={!formInput.LocationIncident || formInput.LocationIncident.trim() === ''}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to extract address components
const extractAddressData = (place) => {
  if (!place) return {};

  let country = '';
  let state = '';
  let county = '';
  let formattedAddress = place.formatted_address || '';

  if (place.address_components) {

    state = place.address_components.find(comp => comp.types.includes('administrative_area_level_1'))?.long_name || '';
    county = place.address_components.find(comp => comp.types.includes('administrative_area_level_2'))?.long_name || '';

    county = county.replace(' County', '').replace(' City', '');  // Remove "County" suffix if present

    // let state_ID = formOptions.states.find(s => s.Name.toLowerCase() === state.toLowerCase())?.ID || 0;
    // let county_ID = formOptions.counties.find(c => c.Name.toLowerCase() === county.toLowerCase() && c.State_ID === state_ID)?.ID || 0;

    // // Extract country, state, and county from address components
    // place.address_components.forEach(component => {
    //   const types = component.types;

    //   if (types.includes('country')) {
    //     country = component.long_name;
    //   } else if (types.includes('administrative_area_level_1')) {
    //     // Use administrative_area_level_1 for state (e.g., "California", "Texas")
    //     state = component.long_name;
    //   } else if (types.includes('administrative_area_level_2') || types.includes('locality')) {
    //     // Use administrative_area_level_2 for county (e.g., "Los Angeles County")
    //     county = component.long_name.replace(' County', '').replace(' City', '');  // Remove "County" suffix if present
    //   }
    // });
  }

  return {
    formattedAddress,
    country,
    state,
    county,
    lat: place.geometry?.location.lat() || 0,
    lng: place.geometry?.location.lng() || 0
  };
};

export default ImportMeta;